import { supabase } from '@/shared/services/supabaseClient';
import { useAuthStore } from '@/shared/stores/authStore';
import type {
  DashboardStats,
  NcrTrendItem,
  NcrBySourceItem,
  ObjectiveProgress,
  AuditLogEntry,
  PendingApproval,
  KpiProgressItem,
} from '../types/dashboard.types';

function getOrgId(): string {
  const orgId = useAuthStore.getState().organizationId;
  if (!orgId) throw new Error('No organization selected');
  return orgId;
}

class DashboardService {
  async getStats(): Promise<DashboardStats> {
    const orgId = getOrgId();

    const today = new Date().toISOString().split('T')[0] ?? '';
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const oneYearAgoStr = oneYearAgo.toISOString().split('T')[0] ?? '';

    // Run all count queries in parallel
    const [ncrResult, ncrHighResult, docResult, kpiResult, objResult, calibResult, supplierEvalResult, complaintsResult, pendingChangesResult, overdueDecisionsResult] = await Promise.all([
      // Open NCRs (not closed or archived)
      supabase
        .from('ncr_records')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .not('status', 'in', '("closed","archived")'),

      // High priority NCRs (open)
      supabase
        .from('ncr_records')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .not('status', 'in', '("closed","archived")')
        .in('priority', ['high', 'critical']),

      // Pending documents (pending_review)
      supabase
        .from('documents')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('status', 'pending_review'),

      // KPI latest measurements status
      supabase
        .from('kpi_measurements')
        .select('status')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('measurement_date', { ascending: false })
        .limit(100),

      // Quality objectives
      supabase
        .from('quality_objectives')
        .select('status')
        .eq('organization_id', orgId)
        .is('deleted_at', null),

      // Overdue calibrations: equipment where next_calibration_date < today
      supabase
        .from('equipment')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .not('status', 'in', '("retired","out_of_service")')
        .lt('next_calibration_date', today),

      // Suppliers eval due: active suppliers with no eval or last eval > 1 year ago
      supabase
        .from('suppliers')
        .select('id, last_evaluation_date')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('status', 'active'),

      // Open complaints (NCRs with type='complaint', not closed/archived)
      supabase
        .from('ncr_records')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .eq('type' as never, 'complaint' as never)
        .is('deleted_at', null)
        .not('status', 'in', '("closed","archived")'),

      // Pending change requests (submitted or under_review)
      supabase
        .from('change_requests')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .in('status', ['submitted', 'under_review']),

      // Overdue review decision actions
      supabase
        .from('review_decisions')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('status' as never, 'open' as never)
        .lt('due_date', today),
    ]);

    // Count KPI statuses
    let kpiOk = 0;
    let kpiWarning = 0;
    let kpiNotOk = 0;
    if (kpiResult.data) {
      for (const m of kpiResult.data) {
        if (m.status === 'ok') kpiOk++;
        else if (m.status === 'warning') kpiWarning++;
        else kpiNotOk++;
      }
    }

    // Count objective statuses
    let objectivesOnTrack = 0;
    const objectivesTotal = objResult.data?.length ?? 0;
    if (objResult.data) {
      for (const o of objResult.data) {
        if (o.status === 'on_track') objectivesOnTrack++;
      }
    }

    return {
      openNcrs: ncrResult.count ?? 0,
      highPriorityNcrs: ncrHighResult.count ?? 0,
      pendingDocuments: docResult.count ?? 0,
      kpiOk,
      kpiWarning,
      kpiNotOk,
      objectivesOnTrack,
      objectivesTotal,
      // Phase 3 stats
      overdueCalibrations: calibResult.count ?? 0,
      pendingChanges: pendingChangesResult.count ?? 0,
      openComplaints: complaintsResult.count ?? 0,
      overdueReviewActions: overdueDecisionsResult.count ?? 0,
      suppliersEvalDue: supplierEvalResult.data
        ? supplierEvalResult.data.filter(
            (s) => !s.last_evaluation_date || s.last_evaluation_date < oneYearAgoStr,
          ).length
        : 0,
    };
  }

  async getNcrTrend(): Promise<NcrTrendItem[]> {
    const orgId = getOrgId();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const { data, error } = await supabase
      .from('ncr_records')
      .select('status, created_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .gte('created_at', sixMonthsAgo.toISOString());

    if (error) throw error;

    // Group by month
    const monthMap = new Map<string, { opened: number; closed: number }>();
    for (const record of data) {
      const date = new Date(record.created_at);
      const key = `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const entry = monthMap.get(key) ?? { opened: 0, closed: 0 };
      entry.opened++;
      if (record.status === 'closed' || record.status === 'archived') {
        entry.closed++;
      }
      monthMap.set(key, entry);
    }

    return Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, counts]) => ({
        month,
        opened: counts.opened,
        closed: counts.closed,
      }));
  }

  async getNcrBySource(): Promise<NcrBySourceItem[]> {
    const orgId = getOrgId();

    const { data, error } = await supabase
      .from('ncr_records')
      .select('source')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .not('status', 'in', '("closed","archived")');

    if (error) throw error;

    const sourceMap = new Map<string, number>();
    for (const record of data) {
      const source = record.source;
      sourceMap.set(source, (sourceMap.get(source) ?? 0) + 1);
    }

    return Array.from(sourceMap.entries()).map(([source, count]) => ({
      source,
      count,
    }));
  }

  async getObjectivesProgress(): Promise<ObjectiveProgress[]> {
    const orgId = getOrgId();

    const { data, error } = await supabase
      .from('quality_objectives')
      .select('id, title, progress_percent, status')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .order('title' as never);

    if (error) throw error;
    return data as ObjectiveProgress[];
  }

  async getRecentActivity(): Promise<AuditLogEntry[]> {
    const orgId = getOrgId();

    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) throw error;
    return data as AuditLogEntry[];
  }

  async getComplianceTrend(): Promise<Array<{ month: string; score: number }>> {
    const orgId = getOrgId();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const { data, error } = await supabase
      .from('kpi_measurements')
      .select('status, measurement_date')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .gte('measurement_date', sixMonthsAgo.toISOString().split('T')[0]);

    if (error) throw error;

    // Group by month and calculate compliance %
    const monthMap = new Map<string, { ok: number; total: number }>();
    for (const m of data) {
      const date = new Date(m.measurement_date);
      const key = `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const entry = monthMap.get(key) ?? { ok: 0, total: 0 };
      entry.total++;
      if (m.status === 'ok') entry.ok++;
      monthMap.set(key, entry);
    }

    return Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, counts]) => ({
        month,
        score: counts.total > 0 ? Math.round((counts.ok / counts.total) * 100) : 0,
      }));
  }

  async getTopKpiProgress(): Promise<KpiProgressItem[]> {
    const orgId = getOrgId();

    // Fetch KPI definitions and their latest measurements
    const [defResult, measResult] = await Promise.all([
      supabase
        .from('kpi_definitions')
        .select('id, name, target_value, warning_threshold, higher_is_better')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('name' as never)
        .limit(10),
      supabase
        .from('kpi_measurements')
        .select('kpi_id, actual_value, measurement_date')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('measurement_date', { ascending: false }),
    ]);

    if (defResult.error) throw defResult.error;
    if (measResult.error) throw measResult.error;

    // Build latest measurement per KPI
    const latestByKpi = new Map<string, number>();
    for (const m of measResult.data) {
      if (!latestByKpi.has(m.kpi_id)) {
        latestByKpi.set(m.kpi_id, m.actual_value);
      }
    }

    // Calculate progress for each KPI that has measurements
    const items: KpiProgressItem[] = [];
    for (const def of defResult.data) {
      const actual = latestByKpi.get(def.id);
      if (actual === undefined || def.target_value === null || def.target_value === 0) continue;

      const higherIsBetter = def.higher_is_better;
      let percent: number;
      if (higherIsBetter) {
        percent = (actual / def.target_value) * 100;
      } else {
        percent = actual === 0 ? 100 : (def.target_value / actual) * 100;
      }
      percent = Math.min(Math.max(Math.round(percent), 0), 100);

      let status: 'ok' | 'warning' | 'not_ok';
      if (higherIsBetter) {
        if (actual >= def.target_value) status = 'ok';
        else if (def.warning_threshold !== null && actual >= def.warning_threshold) status = 'warning';
        else status = 'not_ok';
      } else {
        if (actual <= def.target_value) status = 'ok';
        else if (def.warning_threshold !== null && actual <= def.warning_threshold) status = 'warning';
        else status = 'not_ok';
      }

      items.push({ id: def.id, name: def.name, percent, status });
      if (items.length >= 3) break;
    }

    return items;
  }

  async getPendingApprovals(): Promise<PendingApproval[]> {
    const orgId = getOrgId();

    const { data, error } = await supabase
      .from('documents')
      .select('id, title, status, created_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null)
      .eq('status', 'pending_review')
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) throw error;
    return data.map((doc) => ({
      id: doc.id,
      title: doc.title,
      type: 'document' as const,
      status: doc.status,
      created_at: doc.created_at,
    }));
  }
}

export const dashboardService = new DashboardService();
